import { EpiLink } from '@api/types';
import { useAppDispatch, useAppSelector } from '@hooks/redux/store.hooks';
import { getCurrentPageId } from '@store/app/app.slice';
import { setAllModals } from '@store/modal/modal.slice';
import { getLinkData } from '@utils/links.utils';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';

type ErrorWithPossibleStatus = FetchBaseQueryError | SerializedError | undefined;

function extractStatus(error: ErrorWithPossibleStatus): number[] {
  const statusList: number[] = [];

  if (!error) return statusList;

  if ('status' in error) {
    statusList.push(typeof error.status === 'number' ? error.status : NaN);

    if (typeof error.data === 'object' && error.data !== null && 'status' in error.data) {
      const innerStatus = (error.data as any).status;
      if (typeof innerStatus === 'number') {
        statusList.push(innerStatus);
      }
    }
  }

  if ('originalStatus' in error && typeof error.originalStatus === 'number') {
    statusList.push(error.originalStatus);
  }

  return statusList;
}

export const useReload = () => () => window.location.reload();

export const useScrollToTop = () => () => window.scrollTo({ top: 0, behavior: 'smooth' });

export const useInternalRedirect = () => {
  const scrollToTop = useScrollToTop();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const redirect = (path: string, noScroll?: boolean) => {
    if (noScroll !== true) {
      scrollToTop();
    }
    dispatch(setAllModals(false));
    navigate(path);
  };

  return redirect;
};

export const useLinkData = (link: EpiLink) => {
  const currentPageGuid = useAppSelector(getCurrentPageId);
  const isActivePath = link.contentLink?.guidValue === currentPageGuid;
  return { isActivePath, ...getLinkData(link) };
};

export function useRedirectOnError({
  isError,
  error,
  redirectPath,
  statuses = [403, 404],
}: {
  isError: boolean;
  error: ErrorWithPossibleStatus;
  redirectPath: string;
  statuses?: number[];
}) {
  const redirect = useInternalRedirect();

  useEffect(() => {
    const statusCodes = extractStatus(error);
    const match = statusCodes.some((code) => statuses.includes(code));
    if (isError && match) {
      redirect(redirectPath);
    }
  }, [isError, error, redirect, redirectPath, statuses]);
}
